// src/pages/leads/FiltersBar.jsx
import React from 'react';
import {
  Grid,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Input,
  Checkbox,
  Button,
} from '@chakra-ui/react';

/**
 * FiltersBar
 *
 * Presentational toolbar for the Leads page.
 * Parent owns all state; this component just renders controls and
 * calls the provided change handlers.
 *
 * Props:
 * - filterStatus: string
 * - onChangeStatus: (value: string) => void
 *
 * - filterSeller: string
 * - onChangeSeller: (value: string) => void
 * - salespeople: Array<{ id: string, displayName?: string, name?: string, firstName?: string, lastName?: string, email?: string }>
 *
 * - sortId: string
 * - onChangeSort: (value: string) => void
 *
 * - searchText: string
 * - onChangeSearch: (value: string) => void
 *
 * - hideFinished: boolean
 * - onToggleHideFinished: (checked: boolean) => void
 *
 * - statusOptions: string[]           // e.g., ['NEW','ASSIGNED',...]
 * - sortOptions: Array<{ id: string, label: string }>
 *
 * - onNewLead: () => void
 */
export default function FiltersBar({
  filterStatus,
  onChangeStatus,

  filterSeller,
  onChangeSeller,
  salespeople = [],

  sortId,
  onChangeSort,

  searchText,
  onChangeSearch,

  hideFinished,
  onToggleHideFinished,

  statusOptions = [],
  sortOptions = [],

  onNewLead,
}) {
  return (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(5, minmax(0, 1fr)) 1fr 1fr' }} gap={4}>
      <HStack spacing={4} gridColumn={{ base: '1 / -1', md: '1 / 6' }} flexWrap="wrap">
        {/* Status */}
        <FormControl flex="1" minW={{ base: '100%', md: '200px' }}>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Status
          </FormLabel>
          <Select
            size="md"
            value={filterStatus}
            onChange={(e) => onChangeStatus?.(e.target.value)}
          >
            <option value="ALL">All</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* Sales Rep */}
        <FormControl flex="1" minW={{ base: '100%', md: '220px' }}>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Sales Rep
          </FormLabel>
          <Select
            size="md"
            value={filterSeller}
            onChange={(e) => onChangeSeller?.(e.target.value)}
          >
            <option value="ALL">All owners</option>
            <option value="">Unassigned</option>
            {salespeople.map((sp) => {
              const label =
                sp.displayName ||
                sp.name ||
                `${sp.firstName || ''} ${sp.lastName || ''}`.trim() ||
                sp.email ||
                sp.id;
              return (
                <option key={sp.id} value={sp.id}>
                  {label}
                </option>
              );
            })}
          </Select>
        </FormControl>

        {/* Sort By */}
        <FormControl flex="1" minW={{ base: '100%', md: '220px' }}>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Sort By
          </FormLabel>
          <Select size="md" value={sortId} onChange={(e) => onChangeSort?.(e.target.value)}>
            {sortOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* AI Search */}
        <FormControl flex="1" minW={{ base: '100%', md: '240px' }}>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            AI Search
          </FormLabel>
          <Input
            size="md"
            placeholder="type here"
            value={searchText}
            onChange={(e) => onChangeSearch?.(e.target.value)}
          />
        </FormControl>
      </HStack>

      {/* Right side: Hide Finished + New Lead */}
      <HStack
        spacing={4}
        align="flex-end"
        justify="flex-end"
        gridColumn={{ base: '1 / -1', md: '6 / -1' }}
      >
        <Checkbox
          isChecked={hideFinished}
          onChange={(e) => onToggleHideFinished?.(e.target.checked)}
          colorScheme="blue"
        >
          Hide Finished
        </Checkbox>

        <Button colorScheme="blue" size="md" w="150px" onClick={onNewLead}>
          + New Lead
        </Button>
      </HStack>
    </Grid>
  );
}
